/**
 * LuckyHash Theme - Main JavaScript
 * Cryptocurrency Mining Hardware Store
 */

(function() {
  'use strict';

  // ============================================
  // Utility Functions
  // ============================================
  
  const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  };

  const fetchConfig = (type = 'json') => {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': `application/${type}`
      }
    };
  };

  // ============================================
  // Cart Functionality
  // ============================================

  class Cart {
    constructor() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.toggleBtns = document.querySelectorAll('[data-cart-toggle]');
      this.closeBtns = document.querySelectorAll('[data-cart-close]');
      this.countElements = document.querySelectorAll('[data-cart-count]');
      
      this.bindEvents();
    }

    bindEvents() {
      this.toggleBtns.forEach(btn => {
        btn.addEventListener('click', () => this.open());
      });

      this.closeBtns.forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });

      // Add to cart buttons
      document.addEventListener('click', (e) => {
        const addBtn = e.target.closest('[data-add-to-cart]');
        if (addBtn) {
          e.preventDefault();
          this.addItem(addBtn.dataset.variantId, 1);
        }
      });

      // Quantity buttons in cart
      document.addEventListener('click', (e) => {
        const cartItem = e.target.closest('[data-cart-item]');
        if (!cartItem) return;

        const key = cartItem.dataset.key;
        const qtyValue = cartItem.querySelector('[data-qty-value]');
        const currentQty = parseInt(qtyValue?.value || qtyValue?.textContent || 1);

        if (e.target.closest('[data-qty-decrease]')) {
          this.updateItem(key, Math.max(0, currentQty - 1));
        } else if (e.target.closest('[data-qty-increase]')) {
          this.updateItem(key, currentQty + 1);
        } else if (e.target.closest('[data-remove-item]')) {
          this.updateItem(key, 0);
        }
      });
    }

    isOpen() {
      return this.drawer?.getAttribute('aria-hidden') === 'false';
    }

    open() {
      if (!this.drawer) return;
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    close() {
      if (!this.drawer) return;
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    async addItem(variantId, quantity = 1) {
      try {
        const response = await fetch(window.routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            id: variantId,
            quantity: quantity
          })
        });

        const data = await response.json();
        
        if (data.status === 422) {
          console.error('Error adding to cart:', data.description);
          return;
        }

        await this.refresh();
        this.open();
      } catch (error) {
        console.error('Error adding to cart:', error);
      }
    }

    async updateItem(key, quantity) {
      try {
        const response = await fetch(window.routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            id: key,
            quantity: quantity
          })
        });

        await this.refresh();
      } catch (error) {
        console.error('Error updating cart:', error);
      }
    }

    async refresh() {
      try {
        if (window.location.pathname === '/cart' || window.location.pathname.startsWith('/cart/')) {
          window.location.reload();
          return;
        }

        const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
        const html = await response.text();
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        const newContent = doc.querySelector('[data-cart-content]');
        const newFooter = doc.querySelector('.cart-drawer__footer');
        const newCount = doc.querySelector('[data-cart-count]');
        
        const currentContent = document.querySelector('[data-cart-content]');
        const currentFooter = document.querySelector('.cart-drawer__footer');
        
        if (newContent && currentContent) {
          currentContent.innerHTML = newContent.innerHTML;
        }
        
        if (newFooter && currentFooter) {
          currentFooter.outerHTML = newFooter.outerHTML;
        } else if (!newFooter && currentFooter) {
          currentFooter.remove();
        }
        
        // Update count
        this.countElements.forEach(el => {
          el.textContent = newCount?.textContent || '0';
        });
      } catch (error) {
        console.error('Error refreshing cart:', error);
      }
    }
  }

  // ============================================
  // Mobile Navigation
  // ============================================

  class MobileNav {
    constructor() {
      this.nav = document.querySelector('[data-mobile-nav]');
      this.toggleBtn = document.querySelector('[data-menu-toggle]');
      
      this.bindEvents();
    }

    bindEvents() {
      this.toggleBtn?.addEventListener('click', () => this.toggle());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    isOpen() {
      return this.nav?.getAttribute('aria-hidden') === 'false';
    }

    toggle() {
      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      if (!this.nav) return;
      this.nav.setAttribute('aria-hidden', 'false');
      this.toggleBtn?.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }

    close() {
      if (!this.nav) return;
      this.nav.setAttribute('aria-hidden', 'true');
      this.toggleBtn?.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
  }

  // ============================================
  // Search Overlay
  // ============================================

  class Search {
    constructor() {
      this.overlay = document.querySelector('[data-search-overlay]');
      this.toggleBtns = document.querySelectorAll('[data-search-toggle]');
      this.closeBtns = document.querySelectorAll('[data-search-close]');
      this.input = this.overlay?.querySelector('input');
      
      this.bindEvents();
    }

    bindEvents() {
      this.toggleBtns.forEach(btn => {
        btn.addEventListener('click', () => this.open());
      });

      this.closeBtns.forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
        
        // Open search with Cmd/Ctrl + K
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
          e.preventDefault();
          this.toggle();
        }
      });
    }

    isOpen() {
      return this.overlay?.getAttribute('aria-hidden') === 'false';
    }

    toggle() {
      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      if (!this.overlay) return;
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      // Focus input after animation
      setTimeout(() => this.input?.focus(), 100);
    }

    close() {
      if (!this.overlay) return;
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  }

  // ============================================
  // Product Gallery
  // ============================================

  class ProductGallery {
    constructor(container) {
      this.container = container;
      this.items = container.querySelectorAll('[data-media-id]');
      this.thumbnails = container.querySelectorAll('[data-thumbnail]');
      
      this.bindEvents();
    }

    bindEvents() {
      this.thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const mediaId = thumb.dataset.mediaId;
          this.showMedia(mediaId);
        });
      });
    }

    showMedia(mediaId) {
      // Update main gallery
      this.items.forEach(item => {
        item.classList.toggle('is-active', item.dataset.mediaId === mediaId);
      });

      // Update thumbnails
      this.thumbnails.forEach(thumb => {
        thumb.classList.toggle('is-active', thumb.dataset.mediaId === mediaId);
      });
    }
  }

  // ============================================
  // Product Form
  // ============================================

  class ProductForm {
    constructor(container) {
      this.container = container;
      this.form = container.querySelector('[data-product-form]');
      this.productJson = JSON.parse(container.querySelector('[data-product-json]')?.textContent || '{}');
      this.variantInput = this.form?.querySelector('[data-variant-id]');
      this.optionInputs = this.form?.querySelectorAll('[data-option-input]');
      this.priceElement = container.querySelector('[data-product-price]');
      this.compareElement = container.querySelector('[data-compare-price]');
      this.stickyPriceElement = document.querySelector('[data-sticky-price]');
      this.stickyAddBtn = document.querySelector('[data-sticky-add-btn]');
      this.addBtn = this.form?.querySelector('[data-add-to-cart-btn]');
      this.galleryItems = container.querySelectorAll('[data-media-id]');
      this.galleryThumbnails = container.querySelectorAll('[data-thumbnail]');
      
      this.bindEvents();
    }

    bindEvents() {
      this.optionInputs?.forEach(input => {
        input.addEventListener('change', () => this.onVariantChange());
      });

      // Quantity buttons
      this.form?.addEventListener('click', (e) => {
        const input = this.form.querySelector('[data-quantity-input]');
        if (!input) return;

        const currentQty = parseInt(input.value) || 1;

        if (e.target.closest('[data-qty-decrease]')) {
          input.value = Math.max(1, currentQty - 1);
        } else if (e.target.closest('[data-qty-increase]')) {
          input.value = currentQty + 1;
        }
      });
    }

    onVariantChange() {
      const selectedOptions = [];
      this.optionInputs?.forEach(input => {
        if (input.checked) {
          selectedOptions.push(input.value);
        }
      });

      const variant = this.productJson.variants?.find(v => {
        return v.options.every((option, index) => option === selectedOptions[index]);
      });

      if (variant) {
        this.updateVariant(variant);
      }
    }

    updateVariant(variant) {
      // Update hidden input
      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }

      // Update price
      if (this.priceElement) {
        this.priceElement.textContent = this.formatMoney(variant.price);
      }

      // Update sticky bar price
      if (this.stickyPriceElement) {
        this.stickyPriceElement.textContent = this.formatMoney(variant.price);
      }

      // Update compare price
      if (this.compareElement) {
        if (variant.compare_at_price > variant.price) {
          this.compareElement.textContent = this.formatMoney(variant.compare_at_price);
          this.compareElement.style.display = '';
        } else {
          this.compareElement.style.display = 'none';
        }
      }

      // Update button state
      if (this.addBtn) {
        this.addBtn.disabled = !variant.available;
        this.addBtn.innerHTML = variant.available
          ? `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg> Add to Cart`
          : 'Sold Out';
      }

      // Update sticky bar button state
      if (this.stickyAddBtn) {
        this.stickyAddBtn.disabled = !variant.available;
        this.stickyAddBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      }

      // Update gallery to show variant's featured image
      const mediaId = variant.featured_media?.id || variant.featured_image?.id;
      if (mediaId) {
        this.showMedia(mediaId);
      }

      // Update URL
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    showMedia(mediaId) {
      // Update main gallery items
      this.galleryItems.forEach(item => {
        item.classList.toggle('is-active', item.dataset.mediaId == mediaId);
      });

      // Update thumbnails
      this.galleryThumbnails.forEach(thumb => {
        thumb.classList.toggle('is-active', thumb.dataset.mediaId == mediaId);
      });
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      return `$${amount} CAD`;
    }
  }

  // ============================================
  // Collection Filters
  // ============================================

  class CollectionFilters {
    constructor() {
      this.panel = document.querySelector('[data-filters-panel]');
      this.toggleBtn = document.querySelector('[data-filter-toggle]');
      this.closeBtn = document.querySelector('[data-filter-close]');
      this.form = document.querySelector('[data-filter-form]');
      this.sortSelect = document.querySelector('[data-sort-select]');
      
      this.bindEvents();
    }

    bindEvents() {
      this.toggleBtn?.addEventListener('click', () => this.open());
      this.closeBtn?.addEventListener('click', () => this.close());
      
      this.sortSelect?.addEventListener('change', () => {
        const url = new URL(window.location);
        url.searchParams.set('sort_by', this.sortSelect.value);
        window.location = url;
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    isOpen() {
      return this.panel?.getAttribute('aria-hidden') === 'false';
    }

    open() {
      if (!this.panel) return;
      this.panel.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    close() {
      if (!this.panel) return;
      this.panel.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  }

  // ============================================
  // Animations - Intersection Observer
  // ============================================

  class ScrollAnimations {
    constructor() {
      this.observerOptions = {
        root: null,
        rootMargin: '0px 0px -50px 0px',
        threshold: 0.1
      };

      this.init();
    }

    init() {
      const elements = document.querySelectorAll('.animate-fade-in, .animate-slide-up');
      
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.style.opacity = '1';
              observer.unobserve(entry.target);
            }
          });
        }, this.observerOptions);

        elements.forEach(el => observer.observe(el));
      } else {
        // Fallback for browsers without IntersectionObserver
        elements.forEach(el => el.style.opacity = '1');
      }
    }
  }

  // ============================================
  // Copy Link Button
  // ============================================

  document.addEventListener('click', async (e) => {
    const copyBtn = e.target.closest('[data-copy-link]');
    if (!copyBtn) return;

    try {
      await navigator.clipboard.writeText(window.location.href);
      
      const originalHTML = copyBtn.innerHTML;
      copyBtn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>`;
      
      setTimeout(() => {
        copyBtn.innerHTML = originalHTML;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  });

  // ============================================
  // Initialize
  // ============================================

  document.addEventListener('DOMContentLoaded', () => {
    // Core modules
    new Cart();
    new MobileNav();
    new Search();
    new CollectionFilters();
    new ScrollAnimations();

    // Product page modules
    const productSection = document.querySelector('.product-page');
    if (productSection) {
      const gallery = productSection.querySelector('.product-gallery');
      if (gallery) new ProductGallery(gallery);
      new ProductForm(productSection);
    }
  });

})();
